import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppView from '@common/components/app-view';
import { Button } from '@/components/ui/button';
import { useChatbotTestInboundMutation } from '@common/reducers/backend-api/backend-api';
import { reportAgentEvent } from '@common/services/agent-analytics';

type Bubble = { from: 'wife' | 'bot'; text: string };
type TurnDiag = {
    model: string;
    systemPromptUsed: string;
    historyUsed: { role: string; content: string }[];
    toolCalls: { name: string; inputPreview: string }[];
    latencyMs: number;
    contactMemoryBefore?: string;
    contactMemoryAfter?: string;
};
type Diagnostics = { sendCount: number; latencyMs: number; turns?: TurnDiag[] };

const newSessionKey = () => `test-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

/**
 * Test-chat harness for a quick-agent bot. Plays "the wife": you type messages, the real backend
 * agent pipeline runs in dry-run (nothing is sent to WhatsApp, no credits charged) and the bot's
 * replies + diagnostics are shown. "Burst" sends each newline as a separate rapid message so you
 * can observe whether the bot combines them into one reply.
 */
export const BotTestChatView = () => {
    const { botId } = useParams<{ botId: string }>();
    const navigate = useNavigate();

    const [sessionKey, setSessionKey] = useState<string>(newSessionKey);
    const [transcript, setTranscript] = useState<Bubble[]>([]);
    const [draft, setDraft] = useState('');
    const [lastDiag, setLastDiag] = useState<Diagnostics | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [testInbound, { isLoading }] = useChatbotTestInboundMutation();
    const transcriptEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [transcript, isLoading]);

    const send = useCallback(async (messages: string[]) => {
        if (!botId || messages.length === 0) return;
        setError(null);
        // Durable product event — strong activation signal. No message bodies.
        reportAgentEvent('agent_test_message', 'gemini', { agentId: botId });
        setTranscript((t) => [...t, ...messages.map((m) => ({ from: 'wife' as const, text: m }))]);
        try {
            const res = await testInbound({ botId, sessionKey, messages }).unwrap();
            setTranscript((t) => [...t, ...res.replies.map((r) => ({ from: 'bot' as const, text: r }))]);
            setLastDiag(res.diagnostics);
        } catch (e: any) {
            setError(e?.data?.message || e?.error || 'Request failed');
        }
    }, [botId, sessionKey, testInbound]);

    const onSend = () => { const m = draft.trim(); if (m) { send([m]); setDraft(''); } };
    const onBurst = () => {
        const msgs = draft.split('\n').map((s) => s.trim()).filter(Boolean);
        if (msgs.length) { send(msgs); setDraft(''); }
    };
    const onNewSession = () => { setTranscript([]); setSessionKey(newSessionKey()); setLastDiag(null); setError(null); };

    return (
        <AppView>
            <div className="notranslate" style={{ display: 'flex', gap: 16, padding: 16, height: '100%', boxSizing: 'border-box' }}>
                {/* Conversation */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                        <Button variant="outline" onClick={() => navigate(-1)}>← Back</Button>
                        <Button variant="outline" onClick={onNewSession}>New session</Button>
                        <span style={{ fontSize: 12, opacity: 0.6 }}>session: {sessionKey}</span>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#efeae2' }}>
                        {transcript.length === 0 && (
                            <div style={{ opacity: 0.5, textAlign: 'center', marginTop: 40 }}>
                                Send a message as the wife to start testing the bot.
                            </div>
                        )}
                        {transcript.map((b, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: b.from === 'wife' ? 'flex-end' : 'flex-start', margin: '4px 0' }}>
                                <span style={{
                                    display: 'inline-block', maxWidth: '75%', background: b.from === 'wife' ? '#d9fdd3' : '#fff',
                                    border: '1px solid #d1d7db', borderRadius: 8, padding: '6px 10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                    fontSize: 14, textAlign: 'right', direction: 'rtl',
                                }}>{b.text}</span>
                            </div>
                        ))}
                        {isLoading && <div style={{ opacity: 0.5, fontSize: 13, margin: '4px 0' }}>bot is typing…</div>}
                        {error && <div style={{ color: '#b91c1c', fontSize: 13, margin: '4px 0' }}>Error: {error}</div>}
                        <div ref={transcriptEndRef} />
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
                        <textarea
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
                            rows={2}
                            style={{ flex: 1, resize: 'vertical', padding: 8, borderRadius: 8, border: '1px solid #d1d7db', fontSize: 14 }}
                            placeholder="Type as the wife. Enter = send. For a burst, put each message on its own line, then Burst."
                        />
                        <Button disabled={isLoading} onClick={onSend}>Send</Button>
                        <Button disabled={isLoading} variant="secondary" onClick={onBurst} title="Send each line as a separate rapid message">Burst</Button>
                    </div>
                </div>

                {/* Diagnostics */}
                <div style={{ width: 380, borderLeft: '1px solid #e5e7eb', paddingLeft: 12, overflowY: 'auto' }}>
                    <h3 style={{ marginTop: 0 }}>Diagnostics</h3>
                    {!lastDiag && <p style={{ opacity: 0.5 }}>Send a message to see what the bot saw and did.</p>}
                    {lastDiag && (
                        <div style={{ fontSize: 12 }}>
                            <div style={{ marginBottom: 8 }}>
                                <strong>sendCount:</strong>{' '}
                                <span style={{ color: lastDiag.sendCount > 1 ? '#b91c1c' : '#15803d', fontWeight: 600 }}>{lastDiag.sendCount}</span>
                                {lastDiag.sendCount > 1 && <span style={{ color: '#b91c1c' }}> ← split into multiple replies (bug)</span>}
                                {'  '}·{'  '}<strong>latency:</strong> {lastDiag.latencyMs}ms
                            </div>
                            {(lastDiag.turns ?? []).map((turn, i) => (
                                <details key={i} open={i === (lastDiag.turns!.length - 1)} style={{ marginBottom: 8, border: '1px solid #eee', borderRadius: 6, padding: 8 }}>
                                    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Turn {i + 1} · {turn.model} · {turn.latencyMs}ms</summary>
                                    <div style={{ marginTop: 6 }}>
                                        <div><strong>History the model saw ({turn.historyUsed.length}):</strong></div>
                                        <div style={{ maxHeight: 160, overflowY: 'auto', background: '#fafafa', borderRadius: 4, padding: 6 }}>
                                            {turn.historyUsed.map((h, j) => (
                                                <div key={j} style={{ marginBottom: 2 }}><em>{h.role}:</em> {h.content}</div>
                                            ))}
                                        </div>
                                        {turn.toolCalls.length > 0 && (
                                            <div style={{ marginTop: 6 }}>
                                                <strong>Tool calls:</strong>
                                                {turn.toolCalls.map((tc, j) => <div key={j}>{tc.name}: {tc.inputPreview}</div>)}
                                            </div>
                                        )}
                                        {(turn.contactMemoryBefore || turn.contactMemoryAfter) && (
                                            <div style={{ marginTop: 6 }}>
                                                <strong>Learned memory (this contact):</strong>
                                                <div style={{ background: '#fffbe6', border: '1px solid #f5e1a4', borderRadius: 4, padding: 6, whiteSpace: 'pre-wrap' }}>
                                                    {turn.contactMemoryBefore ? <div style={{ opacity: 0.6 }}><em>before:</em>{'\n'}{turn.contactMemoryBefore}</div> : null}
                                                    <div><em>after:</em>{'\n'}{turn.contactMemoryAfter || '(empty)'}</div>
                                                </div>
                                            </div>
                                        )}
                                        <details style={{ marginTop: 6 }}>
                                            <summary style={{ cursor: 'pointer' }}>System prompt used ({turn.systemPromptUsed.length} chars)</summary>
                                            <pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', borderRadius: 4, padding: 6 }}>{turn.systemPromptUsed}</pre>
                                        </details>
                                    </div>
                                </details>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </AppView>
    );
};

export default BotTestChatView;
